import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { History, RotateCw } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Badge, Button, Callout, Dialog, DialogContent, ErrorState, Money, ShowAsCode, Skeleton, toast } from "@/design";
import { useProjectSlug, useWorkspaceSlug } from "@/app";
import { api, idem, qk, unwrap, type Deployment, type Operation, type Plan } from "@/lib/api";
import { moneyAmount, shortSha } from "@/lib/format";

type Kind = "rollback" | "redeploy";

function planCall(kind: Kind, d: Deployment, project: string, dryRun: boolean, key?: string) {
  if (kind === "rollback") {
    return unwrap(
      api.POST("/v1/deployments/{deployment_id}/rollback", {
        params: { path: { deployment_id: d.id }, header: idem(key), query: dryRun ? { dry_run: true } : undefined },
        body: {},
      }),
    ) as Promise<Plan | Operation>;
  }
  return unwrap(
    api.POST("/v1/projects/{project}/environments/{environment}/services/{service}/deployments", {
      params: {
        path: { project, environment: d.environment_name ?? d.environment_id, service: d.service_name ?? d.service_id },
        header: idem(key),
        query: dryRun ? { dry_run: true } : undefined,
      },
      body: { redeploy_of: d.id, clear_build_cache: false },
    }),
  ) as Promise<Plan | Operation>;
}

export function deployCode(kind: Kind, d: Deployment, project: string) {
  const env = d.environment_name ?? "production";
  const svc = d.service_name ?? "web";
  if (kind === "rollback") {
    return {
      cli: `# Switch ${svc} in ${env} back to ${shortSha(d.source?.commit?.sha)} (no rebuild)\nguv rollback --service ${svc} --env ${env} --to ${d.id}`,
      apiCall: { method: "POST" as const, path: `/v1/deployments/${d.id}/rollback`, body: { reason: "Rolled back from the dashboard" } },
    };
  }
  return {
    cli: `# Redeploy the same image with current settings and secrets\nguv deploy --service ${svc} --env ${env} --redeploy ${d.id}`,
    apiCall: { method: "POST" as const, path: `/v1/projects/${project}/environments/${env}/services/${svc}/deployments`, body: { redeploy_of: d.id } },
  };
}

export interface DeployActionDialogProps {
  kind: Kind;
  deployment: Deployment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Roll back to, or redeploy, a deployment. Shows the dry-run plan first (what changes, cost, whether
 * approval is needed), then runs it and opens the new deploy so you can watch it go live.
 */
export function DeployActionDialog({ kind, deployment: d, open, onOpenChange }: DeployActionDialogProps) {
  const project = useProjectSlug();
  const workspace = useWorkspaceSlug();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [key] = useState(() => crypto.randomUUID());
  const plan = useQuery({
    queryKey: ["plan", kind, d.id],
    queryFn: () => planCall(kind, d, project, true) as Promise<Plan>,
    enabled: open,
    staleTime: 30_000,
  });
  const run = useMutation({
    mutationFn: () => planCall(kind, d, project, false, key) as Promise<Operation>,
    onSuccess: (op) => {
      void qc.invalidateQueries({ queryKey: qk.project(project) });
      onOpenChange(false);
      toast.success(kind === "rollback" ? "Rolling back" : "Redeploying", {
        description: kind === "rollback" ? "Switching back to the earlier image. No rebuild, so it takes seconds." : "Same image, current settings and secrets.",
      });
      const depId = op.resource?.type === "deployment" ? op.resource.id : null;
      if (depId) void navigate({ to: "/$workspace/$project/deployments/$deploymentId", params: { workspace, project, deploymentId: depId }, search: ((s: Record<string, unknown>) => s) as never });
    },
  });
  const code = deployCode(kind, d, project);
  const title = kind === "rollback" ? `Roll back ${d.service_name} to ${shortSha(d.source?.commit?.sha)}?` : `Redeploy ${d.service_name}?`;
  const p = plan.data;
  const delta = p ? moneyAmount(p.cost.delta_monthly) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={title}
        icon={kind === "rollback" ? <History /> : <RotateCw />}
        description={
          kind === "rollback"
            ? `We'll start the image from this deploy in ${d.environment_name} with today's secrets, check it is healthy, then switch traffic. Nothing is rebuilt.`
            : `We'll start this exact image again in ${d.environment_name} with the current settings and secrets. Useful after changing a variable.`
        }
        footer={
          <>
            <ShowAsCode title={title} cli={code.cli} apiCall={code.apiCall} />
            <span className="flex-1" />
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="primary" loading={run.isPending} disabled={!p || plan.isError} onClick={() => run.mutate()}>
              {p?.requires_approval ? "Ask for approval" : kind === "rollback" ? "Roll back" : "Redeploy"}
            </Button>
          </>
        }
      >
        <div className="space-y-3 pb-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-subtle">The plan</div>
          {plan.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : plan.isError ? (
            <ErrorState error={plan.error} onRetry={() => plan.refetch()} />
          ) : p ? (
            <>
              <ul className="space-y-2">
                {p.changes.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-base text-text">
                    <Badge size="sm" tone={c.destructive ? "danger" : "info"} className="mt-0.5 capitalize">
                      {c.action}
                    </Badge>
                    <span>{c.summary}</span>
                  </li>
                ))}
              </ul>
              <PlanFoot>
                Cost change:{" "}
                <strong className="text-text">
                  {delta === 0 ? "none" : <Money value={p.cost.delta_monthly} suffix="/mo" />}
                </strong>
                {p.requires_approval && <> · Production is protected, so an owner or admin must approve this first.</>}
              </PlanFoot>
            </>
          ) : null}
          {run.isError && <ErrorState error={run.error} />}
          {kind === "rollback" && (
            <Callout tone="info">
              Database changes are not rolled back. If a migration ran since this deploy, check it is backwards-compatible first.
            </Callout>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PlanFoot({ children }: { children: ReactNode }) {
  return <p className="text-sm text-muted">{children}</p>;
}
